import React from "react";
import { FaCheck, FaTimes } from "react-icons/fa";
import "./StatusProgress.css";

const StatusProgress = ({ status = "Requested" }) => {
  // Status can be: "Requested", "Verified", "Approved", "Paid", "Declined"
  const getStepStatus = (step) => {
    // If bill is declined, only mark Requested as completed
    if (status === "Declined") {
      return step === "Requested";
    }

    switch (step) {
      case "Requested":
        return ["Requested", "Verified", "Approved", "Paid"].includes(status);
      case "Verified":
        return ["Verified", "Approved", "Paid"].includes(status);
      case "Approved":
        return ["Approved", "Paid"].includes(status);
      case "Paid":
        return status === "Paid";
      default:
        return false;
    }
  };

  // Check if verification is pending
  const isVerificationPending = status === "Requested";
  const isDeclined = status === "Declined";

  // Function to get color based on step
  const getStepColor = (step) => {
    if (isDeclined && step !== "Requested") {
      return "#D3D3D3"; // Lighter gray for inactive steps when declined
    }

    switch (step) {
      case "Requested":
        return "#FFC107"; // Amber - more vibrant and recognizable
      case "Verified":
        return "#FE7743"; // Purple - more distinct from other statuses
      case "Approved":
        return "#4CAF50"; // Keeping the green but could adjust shade if needed
      case "Paid":
        return "#1976D2"; // Deeper blue - more professional
      case "Declined":
        return "#E53935"; // Slightly adjusted red - less harsh
      default:
        return "#BDBDBD"; // Medium gray for default state
    }
  };

  return (
    <div className="stepper-container">
      <div className="stepper">
        {/* Step 1: Bill Requested */}
        <div className="step">
          <div
            className={`step-circle ${
              getStepStatus("Requested") ? "active" : ""
            }`}
            style={
              getStepStatus("Requested")
                ? { backgroundColor: getStepColor("Requested") }
                : {}
            }
          >
            {getStepStatus("Requested") && <FaCheck size={12} />}
          </div>
          <div
            className="step-label"
            style={
              getStepStatus("Requested")
                ? { color: getStepColor("Requested") }
                : {}
            }
          >
            Bill
            <br />
            Requested
          </div>
        </div>

        {/* Connector Line 1-2 */}
        <div
          className={`connector-line ${
            getStepStatus("Verified") ? "active" : ""
          }`}
          style={
            getStepStatus("Verified")
              ? { borderColor: getStepColor("Verified") }
              : {}
          }
        ></div>

        {/* Step 2: Bill Verified or Declined */}
        <div className="step">
          <div
            className={`step-circle ${
              getStepStatus("Verified") || isDeclined ? "active" : ""
            }`}
            style={
              isDeclined
                ? { backgroundColor: getStepColor("Declined") }
                : getStepStatus("Verified")
                ? { backgroundColor: getStepColor("Verified") }
                : {}
            }
          >
            {isDeclined ? (
              <FaTimes size={12} />
            ) : (
              getStepStatus("Verified") && <FaCheck size={12} />
            )}
          </div>
          <div
            className="step-label"
            style={
              isDeclined
                ? { color: getStepColor("Declined") }
                : getStepStatus("Verified")
                ? { color: getStepColor("Verified") }
                : {}
            }
          >
            {isDeclined
              ? "Bill Declined"
              : isVerificationPending
              ? "(Verification Pending)"
              : "Bill Verified"}
          </div>
        </div>

        {/* Connector Line 2-3 */}
        <div
          className={`connector-line ${
            getStepStatus("Approved") ? "active" : ""
          }`}
          style={
            getStepStatus("Approved")
              ? { borderColor: getStepColor("Approved") }
              : {}
          }
        ></div>

        {/* Step 3: Bill Approved */}
        <div className="step">
          <div
            className={`step-circle ${
              getStepStatus("Approved") ? "active" : ""
            }`}
            style={
              getStepStatus("Approved")
                ? { backgroundColor: getStepColor("Approved") }
                : {}
            }
          >
            {getStepStatus("Approved") && <FaCheck size={12} />}
          </div>
          <div
            className="step-label"
            style={
              getStepStatus("Approved")
                ? { color: getStepColor("Approved") }
                : {}
            }
          >
            Bill Approved
          </div>
        </div>

        {/* Connector Line 3-4 */}
        <div
          className={`connector-line ${getStepStatus("Paid") ? "active" : ""}`}
          style={
            getStepStatus("Paid") ? { borderColor: getStepColor("Paid") } : {}
          }
        ></div>

        {/* Step 4: Amount Paid */}
        <div className="step">
          <div
            className={`step-circle ${getStepStatus("Paid") ? "active" : ""}`}
            style={
              getStepStatus("Paid")
                ? { backgroundColor: getStepColor("Paid") }
                : {}
            }
          >
            {getStepStatus("Paid") && <FaCheck size={12} />}
          </div>
          <div
            className="step-label"
            style={getStepStatus("Paid") ? { color: getStepColor("Paid") } : {}}
          >
            Amount paid
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusProgress;
